import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Badge,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { useNavigate } from "react-router-dom";
import LanguageModal from "./LanguageModal";
import { DEFAULT_RECORDS_PER_PAGE, ANSWER_TYPES } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import "react-quill/dist/quill.snow.css";
import AddQuestion from "pages/Courses/AddQuestion";
import ExistingQuestionModal from "./ExistingQuestionModal";

const courseOptions = [
  { label: "Electrical Safety", value: 1 },
  { label: "Power Systems", value: 2 },
];

const batchOptions = [
  { label: "Batch A", value: 1 },
  { label: "Batch B", value: 2 },
];

const QuestionBank = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const [searchState, setSearchState] = useState({
    question: "",
    ansType: null,
    filterParams: [],
  });
  const [pageNum, setPageNum] = useState(1);
  const [submitData, setSubmitData] = useState({
    course: null,
    batch: null,
    questionBankName: "",
  });

  const [qbQuestions, setQbQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [currentQB, setCurrentQB] = useState(null);
  const [uiState, setUiState] = useState({
    languageModal: true,
    qbPanel: {
      isOpen: false,
      mode: "ADD",
    },
    submitModal: false,
    existingModal: false,
  });

  const handleLanguageSave = (lang) => {
    setUiState((prev) => ({
      ...prev,
      languageModal: false,
    }));
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/question-bank/add?lang=${lang}`
        : `/admin/question-bank/add?lang=${lang}`,
    );
  };

  const handleSave = () => {
    showAlert({
      title: "Are you sure you want to create this question bank?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Confirm",
      cancelText: "Cancel",
      onConfirm: () => {
        setUiState((prev) => ({
          ...prev,
          languageModal: false,
        }));
      },
    });
  };

  const handleToggle = () => {
    setUiState((prev) => ({
      ...prev,
      languageModal: false,
    }));
    navigate(
      role === "SUPER_ADMIN"
        ? "/super-admin/question-bank"
        : "/admin/question-bank",
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {};

  const clearSearch = () => {
    setSearchState({
      question: "",
      ansType: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const Option = (props) => {
    const getColor = (value) => {
      switch (value) {
        case "MCQ":
          return "success";
        case "MULTI":
          return "info";
        case "TF":
          return "light";
        default:
          return "warning";
      }
    };

    return (
      <components.Option {...props}>
        <Badge color={getColor(props.data.value)} pill>
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const handleDelete = (index) => {
    showAlert({
      title: "Are you sure you want to delete this question?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {},
    });
  };

  const handleSubmitSave = () => {
    switch (true) {
      case !submitData.course:
        alert("Select course");
        return;

      case !submitData.batch:
        alert("Select batch");
        return;

      case !submitData.questionBankName:
        alert("Enter question bank name");
        return;

      default:
        setUiState((prev) => ({
          ...prev,
          submitModal: false,
        }));
    }
  };

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
    },
    {
      Header: "Question",
      accessor: "question",
      Cell: ({ value }) => (
        <div
          dangerouslySetInnerHTML={{
            __html: value?.english || value?.hindi || ""
          }}
        />
      ),
    },
    {
      Header: "Options",
      accessor: "options",
      Cell: ({ row }) => {
        const { type, options } = row.original;

        // Description → no options
        if (type === "DESC") {
          return <span className="text-muted">—</span>;
        }

        return (
          <div className="max-width-25">
            {options?.map((opt, idx) => (
              <div key={idx} className="d-flex align-items-baseline mb-1">
                <strong className="mr-1">{idx + 1}.</strong>
                <div
                  dangerouslySetInnerHTML={{
                    __html: opt.textEnglish || opt.textHindi || ""
                  }}
                />
              </div>
            ))}
          </div>
        );
      },
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ value }) =>
        ANSWER_TYPES.find((item) => item.value === value)?.label || value,
    },
    {
      Header: "Marks",
      accessor: "marks",
    },
    {
      Header: "Action",
      disableSortBy: true,
      Cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="actions-right d-flex align-items-center">
            {/* Edit */}
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`edit${data.srNo}`}
              size="sm"
            >
              <i className="fas fa-edit table-action-info" />
            </Button>
            <UncontrolledTooltip target={`edit${data.srNo}`}>
              Edit
            </UncontrolledTooltip>

            {/* Divider */}
            <span className="mx-1 text-muted">|</span>

            {/* Delete */}
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`delete${data.srNo}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.index);
              }}
            >
              <i className="fa-regular fa-trash-can table-action-delete" />
            </Button>
            <UncontrolledTooltip target={`delete${data.srNo}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <LanguageModal
        isOpen={uiState.languageModal}
        toggleModal={handleToggle} // prevent closing
        onSave={handleLanguageSave}
      />
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center">
                <i
                  className="fa fa-chevron-left mr-2 cursor"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? "/super-admin/question-bank"
                        : "/admin/question-bank",
                    )
                  }
                />
                <h3 className="mb-0">Question Bank</h3>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-end">
                  <Button
                    className="btn-deep-blue"
                    onClick={() => {
                      setCurrentQB(null);
                      setSelectedIndex(null);

                      setUiState((prev) => ({
                        ...prev,
                        qbPanel: {
                          isOpen: true,
                          mode: "ADD",
                        },
                      }));
                    }}
                  >
                    <i className="fa fa-plus mr-1 cursor" />
                    Question
                  </Button>

                  <Button
                    className="btn-deep-blue mr-5"
                    onClick={() =>
                      setUiState((prev) => ({
                        ...prev,
                        existingModal: true,
                      }))
                    }
                  >
                    <i className="fa fa-plus mr-1 cursor" />
                    Existing Questions
                  </Button>
                  <Button className="btn-deep-blue" onClick={handleSave}>
                    Submit
                  </Button>
                </div>

                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by question"
                      className="mr-2 mb-2 filter-input-large"
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={ANSWER_TYPES}
                      placeholder="Select option type"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      // className="d-flex align-items-center btn-deep-blue mb-0"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>
                    {searchState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>

                  {/* <div className="d-flex align-items-center justify-content-end">
                    <Button className="btn-deep-blue">
                      <i className="fa fa-plus mr-1 cursor" />
                      Question
                    </Button>

                    <Button className="btn-deep-blue">
                      <i className="fa fa-plus mr-1 cursor" />
                      Existing Batch Question
                    </Button>
                  </div> */}
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {qbQuestions.length ? (
                    <span className="total-records">
                      Total Records : {qbQuestions.length}
                    </span>
                  ) : null}
                </div>

                <CommonTable
                  columns={columns}
                  data={qbQuestions.map((q, index) => ({
                    ...q,
                    srNo: index + 1,
                  }))}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    qbQuestions.length / DEFAULT_RECORDS_PER_PAGE,
                  )}
                  showPaginationBottom={true}
                  // rowClick={(row) => navigate(`/admin/user-details/${row.id}`)}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <AddQuestion
        isOpen={uiState.qbPanel.isOpen}
        onClose={() =>
          setUiState((prev) => ({
            ...prev,
            qbPanel: { ...prev.qbPanel, isOpen: false },
          }))
        }
        mode={uiState.qbPanel.mode}
        selectedIndex={selectedIndex}
        initialData={currentQB}
        negativeGrading={false} // or pass from parent if needed
        onSave={(question, index, mode) => {
          setQbQuestions(
            (prev) =>
              mode === "VIEW"
                ? prev.map((q, i) => (i === index ? question : q)) // EDIT
                : [...prev, question], // ADD
          );
        }}
      />

      <Modal
        isOpen={uiState.submitModal}
        toggle={() => setUiState((prev) => ({ ...prev, submitModal: false }))}
      >
        <ModalHeader
          toggle={() => setUiState((prev) => ({ ...prev, submitModal: false }))}
        >
          Create Question Bank
        </ModalHeader>

        <ModalBody>
          {/* Course */}
          <Row>
            {/* Course */}
            <Col md="6">
              <FormGroup>
                <Label>
                  Select Course <span className="text-danger">*</span>
                </Label>
                <Select
                  options={courseOptions}
                  placeholder="Select course"
                  value={submitData.course}
                  onChange={(selected) =>
                    setSubmitData((prev) => ({
                      ...prev,
                      course: selected,
                    }))
                  }
                  styles={fixedWidthCustomStyle()}
                />
              </FormGroup>
            </Col>

            {/* Batch */}
            <Col md="6">
              <FormGroup>
                <Label>
                  Select Batch <span className="text-danger">*</span>
                </Label>
                <Select
                  options={batchOptions}
                  placeholder="Select batch"
                  value={submitData.batch}
                  onChange={(selected) =>
                    setSubmitData((prev) => ({
                      ...prev,
                      batch: selected,
                    }))
                  }
                  styles={fixedWidthCustomStyle()}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Question Bank Name */}
          <FormGroup>
            <Label>
              Question Bank Name <span className="text-danger"> *</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter question bank name"
              value={submitData.questionBankName}
              onChange={(e) =>
                setSubmitData((prev) => ({
                  ...prev,
                  questionBankName: e.target.value,
                }))
              }
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={() =>
              setUiState((prev) => ({ ...prev, submitModal: false }))
            }
          >
            Cancel
          </Button>

          <Button color="deep-blue" onClick={handleSubmitSave}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
      <ExistingQuestionModal
        isOpen={uiState.existingModal}
        toggleModal={() =>
          setUiState((prev) => ({ ...prev, existingModal: false }))
        }
        onSave={() => {
          setUiState((prev) => ({ ...prev, existingModal: false }));
          navigate(
            role === "SUPER_ADMIN"
              ? `/super-admin/question-bank/add/questions`
              : `/admin/question-bank/add/questions`,
          );
        }}
      />
    </>
  );
};

export default QuestionBank;
